import {ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {Contract, ContractAdminListItem, isContractActive} from '@shared/api/models/query/contract.model';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkContractAdminsModal} from '../modals/link-contract-admins.modal';

/**
 * UNIVAPLAT-3518: Contract admins tab of the contract detail page. The detail page only renders
 * it for users holding MANAGE_CONTRACT_ADMINS (general admin); the API enforces the same.
 *
 * - Lists the admins linked to the contract (names and emails are staff data, shown as plain text).
 * - Link / unlink are only offered while the contract is active. The API refuses the admin list
 *   for an inactive contract, so for those the tab shows an informational message and does not load.
 * - Backend owns the user-facing error text; it is shown verbatim in the toast.
 * - Admin names and emails are never logged, and the list is dropped when the tab is destroyed.
 */
@Component({
    selector: 'cdss-contract-admins-tab',
    standalone: true,
    imports: [DatePipe, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './contract-admins-tab.component.html',
    styleUrl: './contract-admins-tab.component.scss'
})
export class ContractAdminsTabComponent implements OnChanges, OnDestroy {
    @Input() contract!: Contract;

    admins: ContractAdminListItem[] = [];
    loading = false;
    loadError: string | null = null;
    /** True while an unlink request runs (double-submit guard; also disables the X buttons). */
    removing = false;

    @ViewChild('linkButton') private linkButton?: ElementRef<HTMLButtonElement>;

    private loadSeq = 0;

    constructor(
        private contractsService: ContractsService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    get isActive(): boolean {
        return !!this.contract && isContractActive(this.contract);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['contract']) {
            return;
        }
        const previous = changes['contract'].previousValue as Contract | undefined;
        const current = this.contract;
        // Reload on first render, a different contract, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isContractActive(previous) !== isContractActive(current)) {
            void this.load();
        }
    }

    ngOnDestroy(): void {
        this.loadSeq++;
        this.admins = [];
    }

    async load(): Promise<void> {
        const seq = ++this.loadSeq;
        this.loadError = null;
        if (!this.contract || !this.isActive) {
            this.admins = [];
            this.loading = false;
            return;
        }
        this.loading = true;
        try {
            const admins = await this.contractsService.listAdmins(this.contract.id);
            if (seq === this.loadSeq) {
                this.admins = admins;
            }
        } catch (error: any) {
            if (seq === this.loadSeq) {
                this.admins = [];
                this.loadError = error?.error?.error ?? 'The contract admins could not be loaded.';
            }
        } finally {
            if (seq === this.loadSeq) {
                this.loading = false;
            }
        }
    }

    adminName(admin: Pick<ContractAdminListItem, 'firstName' | 'lastName'>): string {
        return `${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim();
    }

    trackById(_index: number, admin: ContractAdminListItem): number {
        return admin.id;
    }

    async openLinkModal(): Promise<void> {
        if (!this.isActive) {
            return;
        }
        const ref = this.modalService.open(LinkContractAdminsModal, {
            size: 'lg',
            centered: true,
            ariaLabelledBy: 'link-contract-admins-title'
        });
        ref.componentInstance.contractId = this.contract.id;
        ref.componentInstance.linkedAdmins = this.admins;
        const linked = await ref.result.catch(() => false);
        if (linked) {
            await this.load();
        }
        // NgbModal returns focus to the opener on close; re-assert it after the refresh re-rendered it.
        this.focusLinkButton();
    }

    async unlink(admin: ContractAdminListItem): Promise<void> {
        if (this.removing || !this.isActive) {
            return;
        }
        this.removing = true;
        try {
            await this.contractsService.removeAdmin(this.contract.id, admin.id);
            this.toastr.success('Contract admin unlinked', 'Success');
            await this.load();
            this.focusLinkButton();
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
        } finally {
            this.removing = false;
        }
    }

    private focusLinkButton(): void {
        this.cdr.detectChanges();
        this.linkButton?.nativeElement.focus();
    }
}
